import { useState } from 'react';
import { FaShieldAlt, FaFileContract, FaTimes, FaCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';

/**
 * Consent Modal Component
 * Shows when OAuth users or users without consent try to access the platform
 * Blocks access until both Terms of Service and Privacy Policy are accepted
 */
const ConsentModal = ({ onConsentAccepted }) => {
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!consentAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy to continue');
      return;
    }

    setIsSubmitting(true);
    try {
      await HttpInterceptor.post('/auth/accept-consent', {
        termsAccepted: true,
        privacyAccepted: true,
      });

      toast.success('Consent accepted successfully');
      onConsentAccepted();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept consent. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-6 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaShieldAlt className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Consent Required</h2>
                <p className="text-white/90 text-sm">Please accept our Terms of Service and Privacy Policy to continue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Welcome to Yogvital!</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              To provide you with the best experience and ensure compliance with legal requirements, 
              we need your consent to our Terms of Service and Privacy Policy.
            </p>
          </div>

          {/* Consent Checkbox */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="consentCheckbox"
                checked={consentAccepted}
                onChange={(e) => setConsentAccepted(e.target.checked)}
                className="mt-1 h-5 w-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
              />
              <label htmlFor="consentCheckbox" className="flex-1 text-sm text-gray-700 cursor-pointer">
                I have read and agree to the{' '}
                <Link 
                  to="/terms-of-service?from=consent" 
                  className="text-teal-600 hover:text-teal-700 underline font-medium"
                >
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link 
                  to="/privacy-policy?from=consent" 
                  className="text-teal-600 hover:text-teal-700 underline font-medium"
                >
                  Privacy Policy
                </Link>
                . I understand that by accepting, I am agreeing to be bound by these terms.
                <span className="text-red-500 ml-1">*</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              onClick={handleAccept}
              disabled={!consentAccepted || isSubmitting}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                consentAccepted && !isSubmitting
                  ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white hover:from-teal-700 hover:to-emerald-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Accepting...
                </>
              ) : (
                <>
                  <FaCheck />
                  Accept and Continue
                </>
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            You must accept both documents to use Yogvital. This is a one-time requirement.
          </p>
        </div>
      </div>

      {/* Add CSS animation */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ConsentModal;

